import axios from 'axios';
import { getJwtToken } from '../../../services/auth';

const baseURL =
  process.env.REACT_APP_OPTIFIT_API_BASE_URL ||
  'https://mgwbhc264j.execute-api.us-west-2.amazonaws.com/dev';

const getAuthHeaders = async () => {
  const token = await getJwtToken();
  return {
    accept: 'application/json',
    'Content-Type': 'application/json',
    ...(token ? { Authorization: token } : {}),
  };
};

export type AttributionStatus = 'PENDING_ATTRIBUTION' | 'ATTRIBUTED';

/** Pipe detail nested in optifit_configuration */
export interface OptifitConfigPipeDetail {
  pipe_id: number;
  pipe_name: string;
  pipe_size: number;
  pipe_order: number;
  pipe_wall_thickness: number;
  pipe_length: number;
  number_of_pipes?: number;
  number_of_forges?: number;
  output_assembly_length?: number;
}

/** Optifit configuration for a result */
export interface OptifitConfiguration {
  id: number;
  pipe_id: number;
  thickness: number;
  pipe_wall_thickness: number;
  toe_seam_separation_min_mm: number;
  seam_cap_width_max_mm: number;
  six_oclock_exclusion_deg: number;
  hilo_wps_limit_mm: number;
  excessive_hilo_length_limit_deg: number;
  hilo_preferred_limit_top_mm: number;
  hilo_preferred_limit_middle_mm: number;
  hilo_preferred_limit_lower_mm: number;
  pipe_type: string;
  pipe_mode: string;
  pipe_sequence: string;
  pipe_inner_diameter: number;
  operator_id?: string;
  operator_display_name?: string;
  pipe_detail?: OptifitConfigPipeDetail;
}

/** Nested user when result is attributed to a Data360 user */
export interface OptifitData360User {
  user_id?: number;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone_number?: string | null;
  profile_picture_url?: string | null;
}

export interface OptifitResult {
  optifit_result_id: number;
  project_num: string | null;
  fixed_pipe_name: string | null;
  fixed_pipe_end: string | null;
  free_pipe_name: string | null;
  free_pipe_end: string | null;
  pipe_id: string | number | null;
  optifit_configuration_id?: number | null;
  thickness: number | null;
  pipe_length: number | null;
  recommended_rotation_angle: number | null;
  recommended_hilo: number | null;
  actual_rotation_applied: number | null;
  derived_seam_position: number | null;
  scan_timestamp: string | null;
  optifit_run_timestamp: string | null;
  device_id: string | null;
  operator_type: string | null;
  operator_id: string | null;
  operator_display_name: string | null;
  operator_employee_id: string | null;
  attribution_status: AttributionStatus | string;
  data360_user_id: number | null;
  data360_user?: OptifitData360User | null;
  six_oclock_exclusion: number | null;
  seam_separation_max: number | null;
  seam_separation_exclusion: number | null;
  analysis_seam_position_mm: number | null;
  seam_position_deg: number | null;
  user_changed_seam_position_mm: number | null;
  user_seam_position_deg: number | null;
  top_limit: number | null;
  middle_limit: number | null;
  lower_limit: number | null;
  red_flag: boolean | null;
  best_fit_pos: number | null;
  high_score: any[];
  file_key_a: string | null;
  file_key_b: string | null;
  fixed_end_list_id: number | null;
  free_end_list_id: number | null;
  seam_position: number | null;
  user_seam_position: number | null;
  max_hi_lo: number | null;
  top_zone_max_hilo: number | null;
  middle_zone_max_hilo: number | null;
  lower_zone_max_hilo: number | null;
  tdc: number | null;
  tdc_deg: number | null;
  /** Double-joint seam position (submitted result) — camelCase or snake_case from API */
  djseamposition?: number | null;
  djseampositionDeg?: number | null;
  dj_seam_position?: number | null;
  dj_seam_position_deg?: number | null;
  created_at: string;
  updated_at: string;
  /** Present when API returns full result with config */
  optifit_configuration?: OptifitConfiguration;
}

export interface ListOptifitResultsParams {
  pipe_id?: string | number;
  attribution_status?: AttributionStatus;
  project_num?: string;
}

export interface AssignByResultsPayload {
  result_ids: number[];
  data360_user_id: number;
}

export const optifitResultsApi = {
  async list(params: ListOptifitResultsParams): Promise<OptifitResult[]> {
    const query = new URLSearchParams();
    // if (params.pipe_id !== undefined && params.pipe_id !== null) {
    //   query.append('pipe_id', String(params.pipe_id));
    // }
    if (params.attribution_status) {
      query.append('attribution_status', params.attribution_status);
    }
    if (params.project_num) {
      query.append('project_num', params.project_num);
    }
    const { data } = await axios.get(
      `${baseURL}/data360/optifit/result?${query.toString()}`,
      { headers: await getAuthHeaders() }
    );
    return data?.data?.items ?? [];
  },

  async assignByResults(payload: AssignByResultsPayload): Promise<unknown> {
    const { data } = await axios.post(
      `${baseURL}/data360/optifit/attribution/assignByResults`,
      payload,
      { headers: await getAuthHeaders() }
    );
    return data;
  },
};
